import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RESOURCES } from "./fields";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#00E5FF]";

function Field({ f, value, onChange }) {
  if (f.type === "textarea")
    return <textarea rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
  if (f.type === "select")
    return (
      <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`}>
        <option value="">Select...</option>
        {f.options.map((o) => <option key={o} value={o} className="bg-[#141414]">{o}</option>)}
      </select>
    );
  if (f.type === "bool")
    return (
      <button type="button" onClick={() => onChange(!value)} data-testid={`field-${f.key}`}
        className={`px-4 py-2 text-sm border ${value ? "bg-[#00FF66] text-black border-[#00FF66]" : "border-white/15 text-neutral-400"}`}>
        {value ? "Yes" : "No"}
      </button>
    );
  return <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
}

export default function ResourceManager({ resource }) {
  const cfg = RESOURCES[resource];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    cfg.fields.forEach((f) => {
      if (f.type === "csv" && typeof payload[f.key] === "string")
        payload[f.key] = payload[f.key].split(",").map((s) => s.trim()).filter(Boolean);
    });
    try {
      await api.post(`/admin/${resource}`, payload);
      toast.success(`${cfg.label.slice(0, -1)} created`);
      setOpen(false); setForm({}); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/${resource}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-2xl text-white">{cfg.label} <span className="text-neutral-500 text-base">({items.length})</span></h2>
        <Button onClick={() => { setForm({}); setOpen(true); }} data-testid={`add-${resource}`} className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none"><Plus size={16} /> Add</Button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-[#141414]">
              {cfg.columns.map((c) => <th key={c} className="text-left px-4 py-3 font-accent text-[#00E5FF] text-xs">{c}</th>)}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`row-${resource}-${it.id}`}>
                {cfg.columns.map((c) => (
                  <td key={c} className="px-4 py-3 text-neutral-300">
                    {typeof it[c] === "boolean" ? (it[c] ? "Yes" : "No") : String(it[c] ?? "—").slice(0, 40)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(it.id)} data-testid={`delete-${resource}-${it.id}`} className="text-neutral-500 hover:text-[#FF0055]"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-[#141414] border border-white/15 w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display font-bold text-lg text-white">New {cfg.label.slice(0, -1)}</h3>
              <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              {cfg.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="text-neutral-400 text-xs">{f.label}{f.required && <span className="text-[#FF0055]"> *</span>}</label>
                  <div className="mt-1"><Field f={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} /></div>
                </div>
              ))}
              <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-white/20 bg-transparent">Cancel</Button>
                <Button type="submit" data-testid={`save-${resource}`} className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
